import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProgress } from '../../contexts/ProgressContext';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../../components/ProgressBar';
import SectionCheckbox from '../../components/SectionCheckbox';
import Quiz from '../../components/Quiz';
import { PromptBox } from '../../components/CopyButton';
import PromptPlayground from '../../components/PromptPlayground';
import StepByStepGuide from '../../components/StepByStepGuide';
import DiscordWidget from '../../components/DiscordWidget';
import '../Dashboard.css';
import './Module.css';

const MODULE_ID = 5;

// Quiz sur la création d'apps
const quizQuestions = [
  {
    question: "Qu'est-ce qu'un MVP (Minimum Viable Product) ?",
    options: [
      "La version finale parfaite d'un produit",
      "La version la plus simple qui apporte de la valeur",
      "Un prototype qui ne fonctionne pas",
      "Une maquette Figma interactive"
    ],
    correctAnswer: 1,
    explanation: "Le MVP est la version la plus simple de votre produit qui apporte déjà de la valeur. L'objectif est de lancer vite, tester avec de vrais utilisateurs, puis itérer."
  },
  {
    question: "Combien de fonctionnalités doit avoir un MVP idéalement ?",
    options: [
      "Le plus possible pour impressionner",
      "10-15 fonctionnalités complètes",
      "3-5 fonctionnalités essentielles",
      "Une seule fonctionnalité parfaite"
    ],
    correctAnswer: 2,
    explanation: "Un MVP doit avoir 3-5 fonctionnalités essentielles maximum. Moins c'est plus - concentrez-vous sur ce qui résout vraiment le problème de l'utilisateur."
  },
  {
    question: "Quelle est la règle d'or pour un MVP réussi ?",
    options: [
      "Plusieurs problèmes, plusieurs solutions",
      "1 problème, 1 solution",
      "Aucun problème, juste des features",
      "Tous les problèmes possibles anticipés"
    ],
    correctAnswer: 1,
    explanation: "La règle d'or est '1 problème, 1 solution'. Votre MVP doit résoudre UN seul problème très bien, plutôt que plusieurs problèmes moyennement."
  },
  {
    question: "Quel est le meilleur moment pour faire tester votre app ?",
    options: [
      "Quand elle est 100% terminée et parfaite",
      "Dès que le MVP fonctionne, même imparfait",
      "Jamais, les utilisateurs ne comprennent pas",
      "Après 6 mois de développement minimum"
    ],
    correctAnswer: 1,
    explanation: "Testez dès que possible avec de vrais utilisateurs ! Leur feedback précoce est précieux et vous évite de construire des features dont personne ne veut."
  },
  {
    question: "Quel service est recommandé pour intégrer les paiements ?",
    options: [
      "PayPal uniquement",
      "Virement bancaire manuel",
      "Stripe",
      "Bitcoin seulement"
    ],
    correctAnswer: 2,
    explanation: "Stripe est la solution recommandée : simple à intégrer, supporte cartes, Apple Pay, Google Pay. Claude Code peut l'intégrer facilement dans votre app."
  }
];

// Variables pour le Playground de création de projet
const playgroundVariables = [
  {
    name: 'app_name',
    label: 'Nom de votre application',
    type: 'text',
    placeholder: 'Ex: TaskFlow, MealPlanner, InvoiceBot...'
  },
  {
    name: 'problem',
    label: 'Quel problème résout votre app ?',
    type: 'textarea',
    placeholder: 'Ex: Les freelances perdent du temps à créer leurs factures manuellement chaque mois...'
  },
  {
    name: 'target_user',
    label: 'Utilisateur cible',
    type: 'text',
    placeholder: 'Ex: Freelances, parents, étudiants, PME...'
  },
  {
    name: 'features',
    label: 'Fonctionnalités MVP (3-5 max)',
    type: 'textarea',
    placeholder: 'Ex:\n1. Créer une facture en 30 secondes\n2. Sauvegarder les infos client\n3. Envoyer par email en 1 clic'
  },
  {
    name: 'style',
    label: 'Style visuel souhaité',
    type: 'select',
    options: ['Moderne et minimaliste', 'Corporate et professionnel', 'Fun et coloré', 'Luxueux et premium', 'Tech et futuriste']
  }
];

const playgroundTemplate = `Crée une application web complète avec Claude Code.

📱 **NOM DU PROJET** : {{app_name}}

🎯 **PROBLÈME RÉSOLU** :
{{problem}}

👤 **UTILISATEUR CIBLE** : {{target_user}}

✨ **FONCTIONNALITÉS MVP** :
{{features}}

🎨 **DESIGN** : {{style}}

---

**INSTRUCTIONS POUR CLAUDE CODE** :

1. **Structure du projet**
   - Créer une application React moderne
   - Architecture claire avec composants réutilisables
   - Routing pour la navigation

2. **Pages à créer**
   - Page d'accueil avec proposition de valeur claire
   - Page principale avec la fonctionnalité core
   - Page de paramètres/profil si pertinent

3. **Fonctionnalités techniques**
   - Responsive design (mobile-first)
   - Stockage local des données (localStorage)
   - Animations subtiles pour une UX fluide

4. **Design system**
   - Palette de couleurs cohérente
   - Typographie lisible
   - Composants UI consistants

Commence par créer la structure du projet, puis développe chaque fonctionnalité une par une.`;

// Guide étape par étape pour créer une app complète
const appCreationGuideSteps = [
  {
    title: "Définir clairement le problème",
    content: "Avant de coder, répondez à ces questions essentielles : Quel problème résolvez-vous ? Pour qui ? Pourquoi les solutions existantes ne suffisent pas ?",
    prompt: `Je veux créer une app pour [CIBLE] qui résout le problème de [PROBLÈME].

Aide-moi à valider cette idée :
1. Ce problème est-il assez douloureux pour que les gens paient ?
2. Qui sont mes concurrents et que font-ils mal ?
3. Quelle serait ma proposition de valeur unique ?
4. Comment puis-je simplifier au maximum pour le MVP ?`,
    tips: ["Si vous ne pouvez pas expliquer le problème en une phrase, c'est trop complexe", "Parlez à 5 personnes de votre cible avant de coder"]
  },
  {
    title: "Lister les fonctionnalités MVP",
    content: "Identifiez les 3-5 fonctionnalités absolument nécessaires. Tout le reste attendra la V2. Soyez brutal dans votre priorisation.",
    code: `EXEMPLE - App de gestion de factures :

MVP (V1) - À faire :
✅ Créer une facture (client, items, prix)
✅ Calculer automatiquement les totaux
✅ Exporter en PDF
✅ Sauvegarder localement

V2 - Plus tard :
⏳ Connexion multi-utilisateurs
⏳ Rappels de paiement automatiques
⏳ Intégration comptabilité
⏳ Dashboard analytics`,
    checkpoint: "Vous devez pouvoir lister vos features MVP en moins de 30 secondes"
  },
  {
    title: "Dessiner les écrans principaux",
    content: "Faites des croquis rapides (papier ou Figma) des 3-4 écrans principaux. Pas besoin d'être un designer - l'objectif est de visualiser le parcours utilisateur.",
    tips: ["Un croquis papier de 5 minutes vaut mieux qu'une semaine sur Figma", "Montrez vos croquis à quelqu'un pour valider que c'est compréhensible"],
    prompt: `Décris-moi les écrans principaux de mon app [NOM] :

Pour chaque écran, détaille :
- Titre et objectif de l'écran
- Éléments UI principaux (boutons, formulaires, listes)
- Actions possibles pour l'utilisateur
- Liens vers les autres écrans

Utilise le format wireframe textuel pour que je puisse visualiser.`
  },
  {
    title: "Lancer le développement avec Claude Code",
    content: "Utilisez le brief template pour démarrer. Soyez précis sur le design et les fonctionnalités. Claude Code va créer la structure complète.",
    code: `PROMPT DE DÉMARRAGE :

Crée une application web [NOM] en React.

FONCTIONNALITÉ PRINCIPALE :
[Décrivez en 2-3 phrases]

PARCOURS UTILISATEUR :
1. L'utilisateur arrive sur la page d'accueil
2. Il clique sur [ACTION]
3. Il remplit [FORMULAIRE]
4. Il obtient [RÉSULTAT]

DESIGN :
- Style : [moderne/minimaliste/corporate]
- Couleur principale : [#hex]
- Ambiance : [professionnelle/fun/luxueuse]

Commence par créer la structure de fichiers
et le composant principal.`,
    checkpoint: "Votre premier écran fonctionnel devrait être prêt"
  },
  {
    title: "Itérer et améliorer",
    content: "Une fois le squelette en place, améliorez feature par feature. Testez sur mobile. Demandez des retours. Ne cherchez pas la perfection du premier coup.",
    prompt: `Mon app [NOM] a un bug / besoin d'amélioration :

CONTEXTE :
[Décrivez ce qui existe actuellement]

PROBLÈME / AMÉLIORATION :
[Ce qui ne fonctionne pas ou ce que vous voulez ajouter]

COMPORTEMENT ATTENDU :
[Ce qui devrait se passer]

Corrige/améliore en gardant le reste du code intact.`,
    tips: ["Faites tester par quelqu'un qui ne connaît pas votre app", "Corrigez les bugs critiques avant d'ajouter des features", "Un utilisateur frustré = feedback précieux"]
  }
];

const Module5 = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { progress } = useProgress();
  const navigate = useNavigate();

  if (!isAuthenticated) { navigate('/login'); return null; }

  const handleLogout = () => { logout(); navigate('/'); };
  const totalProgress = progress?.totalProgress || 0;

  return (
    <div className="dashboard-layout">
      <aside className="dashboard-sidebar">
        <div className="sidebar-logo" onClick={() => navigate('/dashboard')}>
          <span className="logo-text">F12IA</span>
          <span className="logo-tm">™</span>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">
            {user?.firstName?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="user-info">
            <span className="user-name">{user?.firstName || 'Utilisateur'}</span>
            <span className="user-role">Membre PRO</span>
          </div>
        </div>

        <nav className="sidebar-nav-pro">
          <div className="nav-label">MENU</div>
          <div className="nav-item" onClick={() => navigate('/dashboard')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg></span>
            <span className="nav-text">Tableau de bord</span>
          </div>

          <div className="nav-label">MODULES</div>
          <div className="nav-item" onClick={() => navigate('/module/1')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg></span>
            <span className="nav-text">1. Introduction IA</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/2')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span>
            <span className="nav-text">2. ChatGPT</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/3')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg></span>
            <span className="nav-text">3. Claude Code</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/4')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg></span>
            <span className="nav-text">4. Automatisation</span>
          </div>
          <div className="nav-item active" onClick={() => navigate('/module/5')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg></span>
            <span className="nav-text">5. Créer des Apps</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/6')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></span>
            <span className="nav-text">6. Projets Avancés</span>
          </div>

          <div className="nav-label">COMPTE</div>
          <div className="nav-item" onClick={() => navigate('/profile')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/></svg></span>
            <span className="nav-text">Mon profil</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/resources')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></span>
            <span className="nav-text">Ressources</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/certificate')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="6"/><path d="M8 14l-2 8 6-3 6 3-2-8"/></svg></span>
            <span className="nav-text">Certificat</span>
          </div>
        </nav>

        <div className="sidebar-progress-pro">
          <div className="progress-header">
            <span className="progress-title">Progression</span>
            <span className="progress-value">{Math.round(totalProgress)}%</span>
          </div>
          <div className="progress-bar-pro">
            <div className="progress-fill-pro" style={{ width: `${totalProgress}%` }} />
          </div>
        </div>

        <div className="sidebar-bottom">
          <div className="nav-item logout-item" onClick={handleLogout}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></span>
            <span className="nav-text">Déconnexion</span>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <div className="module-header">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>← Retour aux programmes</button>
          <div className="module-badge">MODULE 5</div>
          <h1>🚀 Transformer vos idées en projets concrets</h1>
          <p className="module-subtitle">Passer de l'idée à une application fonctionnelle qui génère de la valeur</p>
          <ProgressBar moduleId={MODULE_ID} />
        </div>

        <div className="module-content">

          {/* Section 1: Introduction */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>Introduction : De l'idée au produit</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="intro" />
            </div>

            <p>
              Vous avez des idées. Peut-être beaucoup d'idées. Le problème, c'est de les concrétiser.
              Avant, il fallait des développeurs, du temps, de l'argent. <strong>Aujourd'hui, avec l'IA,
              vous pouvez transformer une idée en prototype fonctionnel en quelques heures.</strong>
            </p>

            <div className="highlight-box">
              <strong>🎯 Objectif du module :</strong> Apprendre la méthodologie pour transformer
              n'importe quelle idée en application fonctionnelle, étape par étape — même sans savoir coder.
            </div>

            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-number">1 jour</div>
                <div className="stat-label">Pour créer un MVP fonctionnel</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">0€</div>
                <div className="stat-label">Coût de développement initial</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">∞</div>
                <div className="stat-label">Idées que vous pouvez tester</div>
              </div>
            </div>
          </section>

          {/* Section 2: Méthode MVP */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>1. La méthode MVP (Minimum Viable Product)</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="mvp" />
            </div>

            <h3>Qu'est-ce qu'un MVP ?</h3>
            <p>
              La version la plus simple de votre produit qui apporte de la valeur.
              <strong> Pas besoin d'être parfait — juste fonctionnel.</strong>
            </p>

            <div className="two-columns">
              <div>
                <h4 style={{color: '#e74c3c'}}>❌ Mauvaise approche</h4>
                <ul className="module-list cross">
                  <li>Passer 6 mois à développer</li>
                  <li>Ajouter toutes les features imaginables</li>
                  <li>Attendre que ce soit "parfait"</li>
                  <li>Ne jamais montrer à personne</li>
                  <li>Abandonner par épuisement</li>
                </ul>
              </div>
              <div>
                <h4 style={{color: '#27ae60'}}>✅ Bonne approche</h4>
                <ul className="module-list check">
                  <li>Lancer en 1-2 semaines</li>
                  <li>3-5 features essentielles max</li>
                  <li>Tester avec de vrais utilisateurs</li>
                  <li>Collecter du feedback rapidement</li>
                  <li>Itérer et améliorer progressivement</li>
                </ul>
              </div>
            </div>

            <h3>La règle du "1 problème, 1 solution"</h3>
            <PromptBox prompt={`❌ TROP COMPLEXE :
"Une app qui fait agenda + todo + notes + finances + fitness + recettes + méditation"

✅ MVP PARFAIT :
"Une app qui envoie un rappel SMS la veille de chaque RDV"

Votre MVP doit résoudre UN seul problème, très bien.
Tout le reste viendra dans les versions futures.`} title="Exemple de focus MVP" />
          </section>

          {/* Section 3: Les 5 étapes */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>2. Les 5 étapes pour créer votre app</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="steps" />
            </div>

            <div className="steps-list">
              <div className="step-item">
                <div className="step-number">1</div>
                <div className="step-content">
                  <strong>Définir le problème</strong>
                  <p>Quel problème précis résolvez-vous ? Pour qui exactement ?</p>
                </div>
              </div>
              <div className="step-item">
                <div className="step-number">2</div>
                <div className="step-content">
                  <strong>Lister les features essentielles</strong>
                  <p>Maximum 3-5 fonctionnalités pour le MVP — soyez brutal</p>
                </div>
              </div>
              <div className="step-item">
                <div className="step-number">3</div>
                <div className="step-content">
                  <strong>Dessiner les écrans</strong>
                  <p>Même un croquis papier de 5 minutes suffit pour commencer</p>
                </div>
              </div>
              <div className="step-item">
                <div className="step-number">4</div>
                <div className="step-content">
                  <strong>Construire avec Claude Code</strong>
                  <p>Décrivez chaque écran et fonctionnalité en détail</p>
                </div>
              </div>
              <div className="step-item">
                <div className="step-number">5</div>
                <div className="step-content">
                  <strong>Tester et itérer</strong>
                  <p>Faites tester par de vrais utilisateurs, récoltez le feedback, améliorez</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Template de brief */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>3. Template de brief pour Claude Code</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="brief" />
            </div>

            <p>Utilisez ce template pour communiquer clairement avec Claude Code :</p>

            <PromptBox prompt={`📋 **BRIEF PROJET - [NOM DE L'APP]**

🎯 **PROBLÈME RÉSOLU** :
[Décrivez en 1-2 phrases le problème que résout votre app]

👤 **UTILISATEUR CIBLE** :
[Qui va utiliser cette app ? Soyez précis]

✨ **FONCTIONNALITÉS MVP** (3-5 max) :
1. [Feature principale - la plus importante]
2. [Feature secondaire]
3. [Feature secondaire]

🚶 **PARCOURS UTILISATEUR** :
1. L'utilisateur arrive sur la page d'accueil et voit...
2. Il clique sur [BOUTON] et...
3. Il remplit [FORMULAIRE/INFO] et...
4. Résultat : il obtient [VALEUR/OUTPUT]

🎨 **DESIGN** :
- Style : [moderne/minimaliste/corporate/fun/luxueux]
- Couleur principale : [couleur ou hex]
- Ambiance : [décrivez en 3 mots]
- Références : [sites/apps similaires si applicable]

📱 **CONTRAINTES TECHNIQUES** :
- Doit fonctionner sur mobile
- Stockage local des données
- [Autres contraintes spécifiques]

Commence par créer la structure du projet et le premier écran.`} title="Le Brief Parfait - Copier et adapter" />

            <div className="tip-box">
              <div className="tip-icon">💡</div>
              <div className="tip-content">
                <h4>Plus vous êtes précis, meilleur est le résultat</h4>
                <p>Ne dites pas "une app de notes". Dites "une app où je peux écrire des notes rapides
                   avec des tags, les rechercher instantanément, et les exporter en markdown".</p>
              </div>
            </div>
          </section>

          {/* Section 5: Exemples de projets */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>4. Exemples de projets réalisables en 1 jour</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="examples" />
            </div>

            <div className="cards-grid">
              <div className="info-card">
                <div className="card-icon">📅</div>
                <h4>Système de réservation</h4>
                <p>Calendrier interactif, créneaux disponibles, confirmation par email</p>
              </div>
              <div className="info-card">
                <div className="card-icon">📝</div>
                <h4>Générateur de contenu</h4>
                <p>Input texte → Traitement IA → Output personnalisé (posts, emails...)</p>
              </div>
              <div className="info-card">
                <div className="card-icon">💰</div>
                <h4>Calculateur/Simulateur</h4>
                <p>Devis automatique, ROI calculator, comparateur de prix</p>
              </div>
              <div className="info-card">
                <div className="card-icon">📊</div>
                <h4>Dashboard analytics</h4>
                <p>Visualisation de données, KPIs, graphiques interactifs</p>
              </div>
              <div className="info-card">
                <div className="card-icon">🎓</div>
                <h4>Plateforme de cours</h4>
                <p>Modules, progression, quiz interactifs, certificats</p>
              </div>
              <div className="info-card">
                <div className="card-icon">🤖</div>
                <h4>Chatbot spécialisé</h4>
                <p>Assistant IA formé sur votre domaine d'expertise</p>
              </div>
            </div>

            <h3>Brief exemple : Générateur de posts LinkedIn</h3>
            <PromptBox prompt={`Crée une application web "PostGenius".

🎯 FONCTIONNALITÉ :
L'utilisateur entre un sujet, l'app génère 3 posts LinkedIn prêts à publier.

🚶 PARCOURS :
1. Page d'accueil avec explication de la valeur + champ de saisie
2. L'utilisateur entre son sujet (ex: "productivité remote")
3. Il choisit un ton : Professionnel / Casual / Inspirant
4. Clic sur "Générer mes posts"
5. Affichage de 3 posts avec bouton "Copier" pour chacun

🎨 DESIGN :
- Style moderne et épuré
- Fond sombre (#1a1a2e)
- Accent bleu LinkedIn (#0077B5)
- Cards avec effet glassmorphism

✨ BONUS :
- Compteur de caractères (max 3000)
- Emojis suggérés pertinents
- Historique des générations précédentes`} title="Exemple complet : PostGenius" />
          </section>

          {/* Section 6: Monétisation */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>5. Monétiser votre création</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="monetize" />
            </div>

            <h3>Modèles de revenus</h3>
            <div className="cards-grid">
              <div className="info-card">
                <div className="card-icon">🆓</div>
                <h4>Freemium</h4>
                <p>Version gratuite limitée + version payante complète</p>
              </div>
              <div className="info-card">
                <div className="card-icon">📆</div>
                <h4>Abonnement</h4>
                <p>Paiement mensuel/annuel récurrent (SaaS)</p>
              </div>
              <div className="info-card">
                <div className="card-icon">💳</div>
                <h4>Paiement unique</h4>
                <p>Accès à vie pour un prix fixe</p>
              </div>
              <div className="info-card">
                <div className="card-icon">📊</div>
                <h4>Pay-per-use</h4>
                <p>Paiement à l'utilisation (crédits, tokens)</p>
              </div>
            </div>

            <h3>Intégrer Stripe pour les paiements</h3>
            <PromptBox prompt={`Intègre Stripe dans mon application [NOM].

MODÈLE : [Abonnement mensuel / Paiement unique / Credits]

PRIX :
- Plan Basic : X€/mois
- Plan Pro : X€/mois
- Plan Enterprise : X€/mois

FONCTIONNALITÉS PAR PLAN :
Basic : [features]
Pro : [features]
Enterprise : [features]

BESOINS :
1. Page de pricing avec les 3 plans
2. Checkout Stripe sécurisé
3. Gestion des abonnements (upgrade/downgrade)
4. Webhooks pour activer les features après paiement
5. Page "Mon compte" avec statut de l'abonnement

Utilise Stripe Checkout pour simplifier.`} title="Prompt d'intégration Stripe" />

            <div className="warning-box">
              <strong>⚠️ Important :</strong> Créez un compte Stripe (gratuit) et obtenez vos clés API
              en mode test avant de développer. Vous ne paierez des frais que sur les vraies transactions.
            </div>
          </section>

          {/* Section 7: Guide pratique */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>6. Atelier : Créez votre app étape par étape</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="guide" />
            </div>

            <p>Suivez ce guide complet pour créer votre première application de A à Z :</p>

            <StepByStepGuide
              moduleId={MODULE_ID}
              guideId="create-app"
              title="De l'idée à l'app fonctionnelle"
              steps={appCreationGuideSteps}
            />
          </section>

          {/* Section 8: Playground */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>7. Playground : Créez votre brief personnalisé</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="playground" />
            </div>

            <p>Remplissez ce formulaire pour générer un brief complet à utiliser avec Claude Code :</p>

            <PromptPlayground
              variables={playgroundVariables}
              template={playgroundTemplate}
              title="Générateur de brief projet"
            />
          </section>

          {/* Checklist de lancement */}
          <section className="module-section">
            <h2>✅ Checklist de lancement</h2>
            <div className="summary-box">
              <ul>
                <li><strong>MVP fonctionnel</strong> — Les features de base marchent sans bugs critiques</li>
                <li><strong>Design propre</strong> — Pas parfait, mais professionnel et cohérent</li>
                <li><strong>Mobile-friendly</strong> — Responsive sur tous les écrans</li>
                <li><strong>Page d'explication</strong> — L'utilisateur comprend la valeur en 5 secondes</li>
                <li><strong>Paiement intégré</strong> — Si applicable (Stripe configuré et testé)</li>
                <li><strong>Analytics</strong> — Pour suivre l'utilisation (Google Analytics ou similaire)</li>
                <li><strong>Contact/Support</strong> — Les utilisateurs peuvent vous joindre</li>
              </ul>
            </div>
          </section>

          {/* Quiz */}
          <section className="module-section">
            <h2>📝 Quiz de validation</h2>
            <p>Testez vos connaissances sur la création d'apps. Score minimum : 70%</p>
            <Quiz
              moduleId={MODULE_ID}
              questions={quizQuestions}
            />
          </section>

          {/* Résumé */}
          <section className="module-section">
            <h2>📋 Résumé du module</h2>
            <div className="summary-box">
              <ul>
                <li><strong>MVP first</strong> — Lancez vite avec le minimum viable, itérez ensuite</li>
                <li><strong>1 problème = 1 solution</strong> — Focus laser sur un seul problème</li>
                <li><strong>Brief détaillé</strong> — Plus vous êtes précis avec Claude Code, meilleur est le résultat</li>
                <li><strong>Testez tôt</strong> — Le feedback utilisateur précoce est précieux</li>
                <li><strong>Monétisez</strong> — Stripe est votre ami pour les paiements</li>
              </ul>
            </div>
          </section>

          {/* Discord */}
          <DiscordWidget />

          {/* Navigation */}
          <div className="module-navigation">
            <button className="btn btn-secondary" onClick={() => navigate('/module/4')}>← Module précédent</button>
            <button className="btn btn-primary" onClick={() => navigate('/module/6')}>Module suivant : Projets Avancés →</button>
          </div>

        </div>
      </main>
    </div>
  );
};

export default Module5;
